import Back from '@sats-group/icons/18/back';
import Location from '@sats-group/icons/18/location';
import Search from '@sats-group/icons/18/search';
import WarningSign from '@sats-group/icons/42/warning-sign';
import Button from '@sats-group/ui-lib/react/button';
import Checkbox from '@sats-group/ui-lib/react/checkbox';
import Chip from '@sats-group/ui-lib/react/chip';
import HiddenInput from '@sats-group/ui-lib/react/hidden-input';
import { useIsMounted } from '@sats-group/ui-lib/react/hooks/use-is-mounted';
import useToggle from '@sats-group/ui-lib/react/hooks/use-toggle';
import Radio from '@sats-group/ui-lib/react/radio';
import Text from '@sats-group/ui-lib/react/text';
import TextInput from '@sats-group/ui-lib/react/text-input';
import React, { useRef, useState } from 'react';
import ClubElementForMap from '~/client/components/club-element-for-map/club-element-for-map';
import type { ClubElementForMap as ClubElementForMapType } from '~/client/components/club-element-for-map/club-element-for-map.types';
import ContentContainer from '~/client/components/content-container/content-container';
import FormDialog from '~/client/components/form-dialog/form-dialog';
import Layout from '~/client/components/layout/layout';
import Map from '~/client/components/map/map';
import type { OpenStatus } from '~/client/components/open-status/open-status.types';
import { mod } from '~/client/helpers/add-bem-modifiers';
import usePolling from '~/client/hooks/use-polling';
import {
  FilterType,
  type ClubsOverviewPage as Props,
} from './clubs-overview-page.types';

const ClubsOverviewPage: React.FunctionComponent<Props> = ({
  clubs,
  description,
  filters,
  layout,
  map,
  noSearchResults,
  openingHoursEndpoint,
  search,
  title,
}) => {
  const [isMapOpen, toggleMap, , closeMapFn] = useToggle(
    map ? map.open : false
  );
  const mapContainer = useRef<HTMLDivElement>(null);
  const [mapOptions, setMapOptions] = useState(map ? map.map.options : {});
  const [openStatuses = {}] =
    usePolling<Record<string, OpenStatus>>(openingHoursEndpoint);

  const viewInMap = (mapOptions: ClubElementForMapType['mapOptions']) => {
    if (mapContainer.current) {
      mapContainer.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }

    setMapOptions(mapOptions);
  };

  const isMounted = useIsMounted();

  const handleLocationClick = (lat: number, lng: number) => {
    if (!mapContainer.current) {
      return;
    }

    if (
      mapContainer.current.clientWidth === document.documentElement.clientWidth
    ) {
      // NOTE: The map is as wide as the document,
      // so the club elements are not next to it and
      // we then don't want to scroll to a club element.
      return;
    }

    // NOTE: By default returns `Element`, but we "know" it'll be an `HTMLElement`.
    // `focus` exists on `HTMLElement`, but not `Element`.
    const club = document.querySelector<HTMLElement>(
      `[data-lat="${lat}"][data-lng="${lng}"]`
    );
    if (club) {
      club.scrollIntoView();
      club.focus({ preventScroll: true });
    }
  };

  const markers = map
    ? map.map.markers.map(marker =>
        Object.assign({}, marker, {
          onClick: () => handleLocationClick(marker.lat, marker.lng),
        })
      )
    : [];

  return (
    <Layout {...layout}>
      <div className="clubs-overview-page">
        <form className="clubs-overview-page__form">
          {map ? (
            <HiddenInput name={map.key} value={String(isMapOpen)} />
          ) : null}
          <div className="clubs-overview-page__hero">
            <ContentContainer theme={ContentContainer.themes.wide}>
              <Text
                className="clubs-overview-page__hero-title"
                elementName="h1"
                theme={Text.themes.headline}
                italic
                size={Text.sizes.headline1}
              >
                {title}
              </Text>
              {description ? (
                <Text
                  className="clubs-overview-page__hero-text"
                  size={Text.sizes.large}
                >
                  {description}
                </Text>
              ) : null}
              <div className="clubs-overview-page__search">
                <div>
                  <TextInput {...search.input} leadingIcon={<Search />} />
                </div>
                <div>
                  <Button
                    text={search.trigger}
                    type="submit"
                    variant={Button.variants.primaryWhite}
                  />
                </div>
              </div>
            </ContentContainer>
          </div>
          <ContentContainer theme={ContentContainer.themes.wide}>
            <section className="clubs-overview-page__actions">
              <div className="clubs-overview-page__filters">
                {filters.selectors.map(entry => (
                  <FormDialog {...entry.dialog} key={entry.dialog.id}>
                    {entry.type === FilterType.Multi
                      ? entry.data.map(checkbox => (
                          <div
                            className="clubs-overview-page__dialog-entry"
                            key={checkbox.value}
                          >
                            <Checkbox {...checkbox} />
                          </div>
                        ))
                      : null}
                    {entry.type === FilterType.Single
                      ? entry.data.map(radio => (
                          <div
                            className="clubs-overview-page__dialog-entry"
                            key={radio.value}
                          >
                            <Radio {...radio} />
                          </div>
                        ))
                      : null}
                  </FormDialog>
                ))}
              </div>
              {map ? (
                <Button
                  disabled={!isMounted}
                  leadingIcon={<Location />}
                  text={isMapOpen ? map.hide : map.show}
                  variant={Button.variants.secondary}
                  onClick={isMounted ? toggleMap : undefined}
                  spinning={!isMounted}
                />
              ) : null}
            </section>
          </ContentContainer>
          {filters.removers.length ? (
            <ContentContainer theme={ContentContainer.themes.wide}>
              <div className="clubs-overview-page__filter-removers">
                {filters.removers.map((entry, index) =>
                  'href' in entry ? (
                    <div key={`${entry.text}-${index}`}>
                      <Chip
                        elementName="a"
                        href={entry.href}
                        icon
                        text={entry.text}
                      />
                    </div>
                  ) : null
                )}
              </div>
            </ContentContainer>
          ) : null}
        </form>
        <ContentContainer theme={ContentContainer.themes.fullToWide}>
          <div
            className={mod('clubs-overview-page__clubs', {
              columns: isMapOpen,
            })}
          >
            <section>
              {clubs.length ? (
                clubs.map(club => (
                  <ClubElementForMap
                    key={club.name}
                    {...club}
                    mapIsOpen={isMapOpen}
                    openStatus={openStatuses[club.id]}
                    showInMap={() => viewInMap(club.mapOptions)}
                  />
                ))
              ) : (
                <div className="clubs-overview-page__no-search-results">
                  <WarningSign />
                  <Text size={Text.sizes.large} tight>
                    {noSearchResults}
                  </Text>
                </div>
              )}
            </section>
            {map && isMapOpen ? (
              <section
                className="clubs-overview-page__map-container"
                ref={mapContainer}
              >
                <Map
                  {...map.map}
                  markers={markers}
                  showMarkers={markers.length ? true : false}
                  options={mapOptions}
                />
                <div className="clubs-overview-page__map-closer">
                  <Button
                    leadingIcon={<Back />}
                    onClick={closeMapFn}
                    text={map.hide}
                    variant={Button.variants.primary}
                  />
                </div>
              </section>
            ) : null}
          </div>
        </ContentContainer>
      </div>
    </Layout>
  );
};

export default ClubsOverviewPage;
